import { createEntityAdapter, createSelector, createSlice, EntityId } from '@reduxjs/toolkit';
import { DeviceVariantsEnum, RadioApTypesEnum, RadioRolesEnum } from '@ubnt/link-core/enums';
import {
  getBestModulationData,
  getWorstModulationData,
} from '@ubnt/link-core/modules/linkSimulation/calculator/WifiUtils';
import { memoize, get, sumBy, map } from 'lodash-es';
import { bandLabelGetter, variantsFullBandNameGetter } from '@ubnt/link-core-ui/utils/deviceTexts';

import {
  countrySupportsBand,
  DEFAULT_COUNTRY,
  getBandByVariant,
  getFrequency,
} from '@ubnt/wifi-calculator/utils/regulatoryLimits';
import {
  calculateMcs,
  calculateMaxMcs,
  calculateCapacity,
  calculateSignal,
  calculatePhyRate,
  getWifiStandard,
  getWifiIEEEStandardBasedOnStandard,
  calculateMaxCapacity,
  isEnumValue,
  isSupportedVariant,
  resolveVariant,
  resolveChannelWidth,
} from '@ubnt/wifi-calculator/utils';
import { getAntennaDegradation } from '@ubnt/wifi-calculator/coverage/antennaPatterns';
import { convertRange, calculateAngle, adjustInitAngle } from '@ubnt/wifi-calculator/utils/math';
import { resolveMountHeight } from '@ubnt/wifi-calculator/mount/resolveMountHeight';
import { deviceInstanceGetter } from '@ubnt/wifi-calculator/device/deviceInstanceGetter';
import type { AppThunk } from 'app/store';
import type { RootState } from 'app/store/reducer';
import { optionalThirdArg, secondArg } from 'app/store/utils';
import {
  getInitialApDeviceState,
  devicesDeviceAdd,
  devicesDeviceByIdSelector,
  devicesDeviceChannelUsageGetter,
  devicesDeviceChannelWidthGetter,
  devicesDeviceCombinedChannelGetter,
  devicesDeviceFrequencyGetter,
  devicesDeviceIsChannelAutoGetter,
  devicesDeviceMapSelector,
  devicesDeviceMloGetter,
  devicesDeviceNameGetter,
  devicesDevicePositionGetter,
  devicesDevicePositionSelector,
  devicesDeviceVariantGetter,
  ApStateDevice,
  StateDevice,
  DevicesMap,
  ExternalDeviceMap,
  ApId,
  ClientId,
  devicesCustomClientDeviceInstanceMapSelector,
  devicesDeviceMountGetter,
  devicesDeviceAntennaGetter,
  devicesDeviceIsOutdoorModeGetter,
  devicesDeviceAfcGetter,
  devicesDeviceApIdsSelector,
  devicesDeviceNameSelector,
  devicesDeviceInstanceSelector,
  devicesMeshDeviceVariantGetter,
  devicesMeshApChannelGetter,
  devicesDeviceVariantSelector,
} from 'modules/devices/slice';
import { AP_ID, CLIENT_ID, MESH_AP_ID } from 'modules/devices/constants';
import { useEditorStore } from 'modules/editor/store';
import { settingsCeilingHeightSelector, settingsCountrySelector } from 'modules/settings/slice';
import { get5GhzVariants, getMeshVariant, getMloVariants, isAp } from 'modules/devices/utils';
import { activeElementLinkIdSelector } from 'modules/activeElement/externalSelectors';
import { throwOrLog } from 'utils/env';
import { resolveMeshDevice } from './utils';

export interface StateLink {
  id: string;
  apId: ApId;
  clientId: ClientId;
}

const linksAdapter = createEntityAdapter<StateLink>();
export const LINK_ID = 'link0';
export const MESH_LINK_ID = 'link1';

const emptyInitialState = linksAdapter.getInitialState();

// Getters
export const linksLinkIdGetter = (link: StateLink) => link.id;
export const linksLinkApIdGetter = (link: StateLink) => link.apId;
export const linksLinkClientIdGetter = (link: StateLink) => link.clientId;

export const linksLinkIsMeshGetter = (devicesMap: DevicesMap, link: StateLink) => {
  const clientId = linksLinkClientIdGetter(link);
  const clientDevice = devicesMap[clientId];
  return isAp(clientDevice);
};

export const linksMeshUplinkByApIdGetter = (linksMap: Record<EntityId, StateLink>, apId: string) => {
  return Object.values(linksMap).find((linkValue) => {
    const clientId = linksLinkClientIdGetter(linkValue);
    return clientId === apId;
  });
};

export const linksLinkMeshUplinkGetter = (linksMap: Record<EntityId, StateLink>, link: StateLink) => {
  const apId = linksLinkApIdGetter(link);
  return linksMeshUplinkByApIdGetter(linksMap, apId);
};

export const linksHasMeshUplinkGetter = (linksMap: Record<EntityId, StateLink>, link: StateLink) =>
  !!linksLinkMeshUplinkGetter(linksMap, link);

export const linksMeshUplinkApIdGetter = (linksMap: Record<EntityId, StateLink>, link: StateLink) => {
  const uplink = linksLinkMeshUplinkGetter(linksMap, link);
  if (!uplink) {
    return undefined;
  }
  return linksLinkApIdGetter(uplink);
};

export const linksMeshUplinkIdGetter = (linksMap: Record<EntityId, StateLink>, link: StateLink) => {
  const uplink = linksLinkMeshUplinkGetter(linksMap, link);
  if (!uplink) {
    return undefined;
  }

  return linksLinkIdGetter(uplink);
};

export const getDistance = (
  apPosition: StateDevice['position'],
  clientPosition: StateDevice['position'],
  mount?: ApStateDevice['mount'],
  clientMount?: ApStateDevice['mount'],
  ceilingHeight?: number,
  ignoreMountHeight?: boolean
) => {
  return Math.sqrt(
    (clientPosition.x - apPosition.x) ** 2 +
      (clientPosition.y - apPosition.y) ** 2 +
      (ignoreMountHeight ? 0 : resolveMountHeight(mount, clientMount, ceilingHeight) ** 2)
  );
};

export const linksLinkDistanceGetter = (devicesMap: DevicesMap, link: StateLink, ceilingHeight?: number) => {
  const apId = linksLinkApIdGetter(link);
  const clientId = linksLinkClientIdGetter(link);
  const apStateDevice = devicesMap[apId];
  const mount = devicesDeviceMountGetter(apStateDevice);
  const apPosition = devicesDevicePositionGetter(apStateDevice);
  const clientStateDevice = devicesMap[clientId];
  const clientPosition = devicesDevicePositionGetter(clientStateDevice);
  const clientMount = isAp(clientStateDevice) ? devicesDeviceMountGetter(clientStateDevice) : undefined;
  if (apPosition && clientPosition) {
    return getDistance(apPosition, clientPosition, mount, clientMount, ceilingHeight);
  }
  return 0;
};

export const linksDeviceChannelWidthGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ApStateDevice['variant'],
  externalDeviceMap?: ExternalDeviceMap
): number | undefined => {
  const apId = linksLinkApIdGetter(link);
  const device = devicesMap[apId];
  const variant = variantArg ?? devicesDeviceVariantGetter(device);
  if (variant === devicesMeshDeviceVariantGetter(devicesMap)) {
    const uplink = linksLinkMeshUplinkGetter(linksMap, link);
    if (uplink) {
      const deviceName = devicesDeviceNameGetter(device);
      const defaultDeviceInstance = deviceInstanceGetter(deviceName, undefined, externalDeviceMap);
      const deviceInstance = deviceInstanceGetter(
        deviceName,
        resolveVariant(defaultDeviceInstance.variants, variant),
        externalDeviceMap
      );
      const uplinkDeviceName = devicesDeviceNameGetter(devicesMap[linksLinkApIdGetter(uplink)]);
      const uplinkDefaultInstance = deviceInstanceGetter(uplinkDeviceName);
      const uplinkVariant = resolveVariant(uplinkDefaultInstance.variants, variant);
      return resolveChannelWidth(
        deviceInstance.getChannelWidths(RadioApTypesEnum.Ptmp),
        linksDeviceChannelWidthGetter(devicesMap, linksMap, uplink, uplinkVariant, externalDeviceMap)
      );
    }
  }
  return devicesDeviceChannelWidthGetter(device, variant);
};

export const linksDeviceChannelGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ApStateDevice['variant'],
  country?: string,
  externalDeviceMap?: ExternalDeviceMap
): number | undefined => {
  const apId = linksLinkApIdGetter(link);
  const device = devicesMap[apId];
  const variant = variantArg ?? devicesDeviceVariantGetter(device);
  if (variant === devicesMeshDeviceVariantGetter(devicesMap)) {
    const uplink = linksLinkMeshUplinkGetter(linksMap, link);
    if (uplink) {
      const uplinkDeviceName = devicesDeviceNameGetter(devicesMap[linksLinkApIdGetter(uplink)]);
      const uplinkDefaultInstance = deviceInstanceGetter(uplinkDeviceName);
      const uplinkVariant = resolveVariant(uplinkDefaultInstance.variants, variant);
      return linksDeviceChannelGetter(devicesMap, linksMap, uplink, uplinkVariant, country, externalDeviceMap);
    }
  }
  const channelWidth = linksDeviceChannelWidthGetter(devicesMap, linksMap, link, variant, externalDeviceMap);
  return devicesDeviceCombinedChannelGetter(device, variant, channelWidth, country, externalDeviceMap);
};

export const linksIsChannelAutoGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ApStateDevice['variant']
): boolean => {
  const apId = linksLinkApIdGetter(link);
  const device = devicesMap[apId];
  const variant = variantArg ?? devicesDeviceVariantGetter(device);
  if (variant === devicesMeshDeviceVariantGetter(devicesMap)) {
    const uplink = linksLinkMeshUplinkGetter(linksMap, link);
    if (uplink) {
      const uplinkDeviceName = devicesDeviceNameGetter(devicesMap[linksLinkApIdGetter(uplink)]);
      const uplinkDefaultInstance = deviceInstanceGetter(uplinkDeviceName);
      const uplinkVariant = resolveVariant(uplinkDefaultInstance.variants, variant);
      return linksIsChannelAutoGetter(devicesMap, linksMap, uplink, uplinkVariant);
    }
  }
  return devicesDeviceIsChannelAutoGetter(device, variant);
};

export const linksDeviceFrequencyGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ApStateDevice['variant'],
  country?: string,
  externalDeviceMap?: ExternalDeviceMap
): number | undefined => {
  const apId = linksLinkApIdGetter(link);
  const device = devicesMap[apId];
  const deviceInstance = deviceInstanceGetter(devicesDeviceNameGetter(device), undefined, externalDeviceMap);
  const variant = resolveVariant(deviceInstance.variants, variantArg) ?? devicesDeviceVariantGetter(device);
  if (variant === devicesMeshDeviceVariantGetter(devicesMap)) {
    const uplink = linksLinkMeshUplinkGetter(linksMap, link);
    if (uplink) {
      const uplinkDeviceName = devicesDeviceNameGetter(devicesMap[linksLinkApIdGetter(uplink)]);
      const uplinkDefaultInstance = deviceInstanceGetter(uplinkDeviceName);
      const uplinkVariant = resolveVariant(uplinkDefaultInstance.variants, variant);
      return linksDeviceFrequencyGetter(devicesMap, linksMap, uplink, uplinkVariant, country, externalDeviceMap);
    }
  }
  return devicesDeviceFrequencyGetter(device, variant, country, externalDeviceMap);
};

const STATIC_POSITION = { x: 0, y: 0 };

const getStaticDevicesMap = (devicesMap: DevicesMap): DevicesMap => {
  const result: Record<EntityId, StateDevice> = {};
  for (const key in devicesMap) {
    result[key] = { ...devicesMap[key], position: STATIC_POSITION };
  }
  return result as DevicesMap;
};

export const linksLinkDevicesInfoGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ApStateDevice['variant'],
  country?: string,
  externalDeviceMap?: ExternalDeviceMap
) =>
  linksLinkDevicesInfoGetterMemoized(
    getStaticDevicesMap(devicesMap),
    linksMap,
    link,
    variantArg,
    country,
    externalDeviceMap
  );

export const linksLinkDevicesInfoGetterMemoized = memoize(
  (
    devicesMap: DevicesMap,
    linksMap: Record<EntityId, StateLink>,
    link: StateLink,
    variantArg?: ApStateDevice['variant'],
    country?: string,
    externalDeviceMap?: ExternalDeviceMap
  ) => {
    const apId = linksLinkApIdGetter(link);
    const clientId = linksLinkClientIdGetter(link);
    const stateApDevice = devicesMap[apId];
    const stateClientDevice = devicesMap[clientId];
    const isMesh = linksLinkIsMeshGetter(devicesMap, link);
    const deviceName = devicesDeviceNameGetter(stateApDevice);
    const apDefaultDevice = deviceInstanceGetter(deviceName);
    const meshChannel = linksDeviceChannelGetter(devicesMap, linksMap, link, undefined, country, externalDeviceMap);
    const variant =
      variantArg ??
      (isMesh ? getMeshVariant(apDefaultDevice.variants, meshChannel) : devicesDeviceVariantGetter(stateApDevice));
    const channel = linksDeviceChannelGetter(devicesMap, linksMap, link, variant, country, externalDeviceMap);
    const apDevice = deviceInstanceGetter(deviceName, resolveVariant(apDefaultDevice.variants, variant, channel));
    const defaultApDevice = deviceInstanceGetter(deviceName, undefined, externalDeviceMap);
    const clientDeviceName = devicesDeviceNameGetter(stateClientDevice);
    const defaultClientDevice = deviceInstanceGetter(clientDeviceName, undefined, externalDeviceMap);
    const clientDevice = deviceInstanceGetter(
      clientDeviceName,
      resolveVariant(defaultClientDevice.variants, variant),
      externalDeviceMap
    );
    const apChannelWidth = linksDeviceChannelWidthGetter(devicesMap, linksMap, link, variant, externalDeviceMap);
    const channelWidth = resolveChannelWidth(clientDevice.getChannelWidths(RadioApTypesEnum.Ptmp), apChannelWidth);
    const channelUsage = devicesDeviceChannelUsageGetter(stateApDevice, variant);
    const mlo = devicesDeviceMloGetter(stateApDevice);
    const channelsMap = apDevice.channels;
    const isOutdoorMode = devicesDeviceIsOutdoorModeGetter(stateApDevice);
    const isAfcEnabled = devicesDeviceAfcGetter(stateApDevice);
    const mloVariants = getMloVariants(defaultApDevice.mloVariants, variant)?.filter((variant) =>
      countrySupportsBand(country ?? DEFAULT_COUNTRY, getBandByVariant(variant), isOutdoorMode, isAfcEnabled)
    );
    const wifiStandard = getWifiStandard(apDevice, clientDevice);

    return {
      variant,
      stateApDevice,
      apDevice,
      defaultApDevice,
      stateClientDevice,
      clientDevice,
      clientDeviceName,
      channelWidth,
      apChannelWidth,
      channelUsage,
      channelsMap,
      channel,
      wifiStandard,
      mlo,
      mloVariants,
    };
  },
  (devicesMap, linksMap, link, variantArg, country, externalDeviceMap) =>
    JSON.stringify({ devicesMap, linksMap, link, variantArg, country, externalDeviceMap })
);

export const linksClientLinkGetter = (links: StateLink[]) =>
  links.find((link) => linksLinkClientIdGetter(link) === CLIENT_ID)!;

const linksPointerActiveLinkGetter = (
  apIds: ApId[],
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  clientLink: StateLink,
  externalDeviceMap: ExternalDeviceMap,
  country: string,
  ceilingHeight: number
) => {
  if (apIds.length === 1) {
    return clientLink;
  }

  let bestSignal: number | null = null;

  return apIds.reduce((link: StateLink, apId) => {
    const apDevice = devicesMap[apId];
    const apDeviceInstance = deviceInstanceGetter(devicesDeviceNameGetter(apDevice));
    const variant = devicesDeviceVariantGetter(apDevice);
    if (!isSupportedVariant(apDeviceInstance.variants, variant)) {
      return link;
    }

    const apLink: StateLink = { ...clientLink, apId };
    const apSignal = linksLinkSignalGetter(
      devicesMap,
      linksMap,
      apLink,
      variant,
      country,
      externalDeviceMap,
      ceilingHeight
    );

    if (apSignal != null && (bestSignal === null || apSignal > bestSignal)) {
      bestSignal = apSignal;
      return apLink;
    }

    return link;
  }, clientLink);
};

export const linkAngleGetter = (devicesMap: DevicesMap, link: StateLink, reverse = false) => {
  const [apId, clientId] =
    reverse ?
      [linksLinkClientIdGetter(link), linksLinkApIdGetter(link)]
    : [linksLinkApIdGetter(link), linksLinkClientIdGetter(link)];
  const apDevice = devicesMap[apId];
  if (!isAp(apDevice)) {
    return 0;
  }
  const apPosition = devicesDevicePositionGetter(apDevice);
  const clientPosition = devicesDevicePositionGetter(devicesMap[clientId]);
  const mount = devicesDeviceMountGetter(apDevice);
  const initAngle = calculateAngle([apPosition.x, apPosition.y], [clientPosition.x, clientPosition.y]);
  return adjustInitAngle(initAngle, mount);
};

export const linksLinkSignalGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ApStateDevice['variant'],
  country?: string,
  externalDeviceMap?: ExternalDeviceMap,
  ceilingHeight?: number
) => {
  const devicesInfo = linksLinkDevicesInfoGetter(devicesMap, linksMap, link, variantArg, country, externalDeviceMap);
  const {
    apDevice,
    defaultApDevice,
    stateApDevice,
    clientDevice,
    stateClientDevice,
    apChannelWidth,
    channelsMap,
    channel,
    variant,
  } = devicesInfo;

  if (channel && channelsMap && apDevice && clientDevice && apChannelWidth) {
    const txPower = apDevice.maxTxPower;
    const supportsAfc = defaultApDevice.supportsAfc;
    const apAntenna = devicesDeviceAntennaGetter(stateApDevice, resolveVariant(defaultApDevice.variants, variant));
    const txGain = apAntenna.gain;
    // For client devices, use the antenna from the Device instance directly
    // For mesh clients (which are APs), use devicesDeviceAntennaGetter
    const clientAntenna =
      stateClientDevice.role === RadioRolesEnum.Ap ?
        devicesDeviceAntennaGetter(stateClientDevice, resolveVariant(clientDevice.variants, variant), externalDeviceMap)
      : clientDevice.antenna;
    const rxGain = clientAntenna.gain;
    if (txPower == null || txGain == null || rxGain == null) {
      return undefined;
    }

    const channelValue = channelsMap[channel];
    const frequency = getFrequency(channelValue);
    const distance = linksLinkDistanceGetter(devicesMap, link, ceilingHeight);
    const angle = linkAngleGetter(devicesMap, link);
    const mount = devicesDeviceMountGetter(stateApDevice);
    const linkIsMesh = linksLinkIsMeshGetter(devicesMap, link);
    const clientMount =
      linkIsMesh && stateClientDevice.role === RadioRolesEnum.Ap ?
        devicesDeviceMountGetter(stateClientDevice)
      : undefined;
    const mountHeight = resolveMountHeight(mount, clientMount, ceilingHeight);
    let antennaDegradation = getAntennaDegradation({
      antenna: apAntenna,
      mount,
      mountHeight,
      distance,
      initAzimuth: angle,
      logger: throwOrLog,
    });

    if (linkIsMesh && clientMount != null) {
      const reverseAngle = linkAngleGetter(devicesMap, link, true);
      const rxAntennaDegradation = getAntennaDegradation({
        antenna: clientAntenna,
        mount: clientMount,
        mountHeight: -mountHeight,
        distance,
        initAzimuth: reverseAngle,
        logger: throwOrLog,
      });

      // we cap mesh antenna degradation at -20dB to compensate a bit the fact
      // that we don't take into account signal reflections (matches Design Center behavior)
      antennaDegradation += Math.max(rxAntennaDegradation, -20);
    }

    const isOutdoor = devicesDeviceIsOutdoorModeGetter(stateApDevice);
    const afc = devicesDeviceAfcGetter(stateApDevice);

    return calculateSignal({
      txPower,
      txGain,
      rxGain,
      frequency,
      distance,
      apChannelWidth,
      antennaDegradation,
      country,
      supportsAfc: supportsAfc && afc,
      isOutdoor,
    });
  }
  return undefined;
};

export const linksLinkMinSignalGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  externalDeviceMap?: ExternalDeviceMap
) => {
  const { channelWidth, apDevice, clientDevice } = linksLinkDevicesInfoGetter(
    devicesMap,
    linksMap,
    link,
    undefined,
    undefined,
    externalDeviceMap
  );

  if (!channelWidth) {
    return undefined;
  }

  const modulationData = getWorstModulationData(channelWidth, apDevice, clientDevice);
  return modulationData.rxSensitivityThd;
};

export const linksLinkMaxSignalGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  externalDeviceMap?: ExternalDeviceMap
) => {
  const devicesInfo = linksLinkDevicesInfoGetter(devicesMap, linksMap, link, undefined, undefined, externalDeviceMap);

  if (!devicesInfo?.channelWidth) {
    return undefined;
  }

  const { channelWidth, apDevice, clientDevice } = devicesInfo;
  const modulationData = getBestModulationData(channelWidth, apDevice, clientDevice);
  return modulationData.rxSensitivityThd;
};

export const linksLinkMcsGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ValuesOf<typeof DeviceVariantsEnum>,
  country?: string,
  externalDeviceMap?: ExternalDeviceMap,
  ceilingHeight?: number
) => {
  const signal = linksLinkSignalGetter(
    devicesMap,
    linksMap,
    link,
    variantArg,
    country,
    externalDeviceMap,
    ceilingHeight
  );

  if (signal == null) {
    return undefined;
  }

  const { channelWidth, apDevice, clientDevice } = linksLinkDevicesInfoGetter(
    devicesMap,
    linksMap,
    link,
    variantArg,
    country,
    externalDeviceMap
  );

  if (!channelWidth) {
    return undefined;
  }

  return calculateMcs(signal, channelWidth, apDevice, clientDevice);
};

export const linksLinkPhyRateGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ValuesOf<typeof DeviceVariantsEnum>,
  country?: string,
  externalDeviceMap?: ExternalDeviceMap,
  ceilingHeight?: number
) => {
  const signal = linksLinkSignalGetter(
    devicesMap,
    linksMap,
    link,
    variantArg,
    country,
    externalDeviceMap,
    ceilingHeight
  );

  if (signal == null) {
    return undefined;
  }

  const { channelWidth, apDevice, clientDevice } = linksLinkDevicesInfoGetter(
    devicesMap,
    linksMap,
    link,
    variantArg,
    country,
    externalDeviceMap
  );

  if (!channelWidth) {
    return undefined;
  }

  return calculatePhyRate(signal, channelWidth, apDevice, clientDevice);
};

export interface PhyRateEntry {
  phyRate: number;
  mcs: number;
  bandLabel: string;
}

export const resolveLinkVariant = (devicesMap: DevicesMap, link: StateLink, externalDeviceMap?: ExternalDeviceMap) => {
  const linkIsMesh = linksLinkIsMeshGetter(devicesMap, link);
  const apId = linksLinkApIdGetter(link);
  const apDevice = devicesMap[apId];
  const apVariant = devicesDeviceVariantGetter(apDevice);
  const clientId = linksLinkClientIdGetter(link);
  const apDeviceName = devicesDeviceNameGetter(apDevice);
  const apVariants = deviceInstanceGetter(apDeviceName).variants;
  const clientDeviceName = devicesDeviceNameGetter(devicesMap[clientId]);
  const clientVariants = deviceInstanceGetter(clientDeviceName, undefined, externalDeviceMap).variants;
  const meshChannel = devicesMeshApChannelGetter(devicesMap);

  return (
    linkIsMesh ? getMeshVariant(apVariants, meshChannel)
    : isSupportedVariant(clientVariants, apVariant) ? apVariant
    : undefined
  );
};

export const resolveLinkApVariant = (devicesMap: DevicesMap, link: StateLink) => {
  const apId = linksLinkApIdGetter(link);
  const apDevice = devicesMap[apId];
  return devicesDeviceVariantGetter(apDevice);
};

export const linksLinkPhyRatesMapGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  country?: string,
  externalDeviceMap?: ExternalDeviceMap,
  ceilingHeight?: number
): PhyRateEntry[] => {
  const linkIsMesh = linksLinkIsMeshGetter(devicesMap, link);
  const variant = resolveLinkVariant(devicesMap, link, externalDeviceMap);
  const { mlo, mloVariants, apDevice, defaultApDevice } = linksLinkDevicesInfoGetter(
    devicesMap,
    linksMap,
    link,
    variant,
    country,
    externalDeviceMap
  );
  if (!mlo || !mloVariants || linkIsMesh) {
    const bandLabel = bandLabelGetter(apDevice);
    const mcs = linksLinkMcsGetter(devicesMap, linksMap, link, variant, country, externalDeviceMap, ceilingHeight);
    const phyRate = linksLinkPhyRateGetter(
      devicesMap,
      linksMap,
      link,
      variant,
      country,
      externalDeviceMap,
      ceilingHeight
    );
    if (!bandLabel || mcs == null || phyRate == null) {
      return [];
    }
    return [{ bandLabel, mcs, phyRate }];
  }
  const phyRates: PhyRateEntry[] = [];
  mloVariants.forEach((variant) => {
    const variantDevice = deviceInstanceGetter(defaultApDevice.name, variant, externalDeviceMap);
    const bandLabel = bandLabelGetter(variantDevice);
    const mcs = linksLinkMcsGetter(devicesMap, linksMap, link, variant, country, externalDeviceMap, ceilingHeight);
    const phyRate = linksLinkPhyRateGetter(
      devicesMap,
      linksMap,
      link,
      variant,
      country,
      externalDeviceMap,
      ceilingHeight
    );
    if (bandLabel && mcs != null && phyRate != null) {
      phyRates.push({ bandLabel, mcs, phyRate });
    }
  });
  return phyRates.sort((a, b) => b.bandLabel.localeCompare(a.bandLabel));
};

export const linksLinkAirSpeedsGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  country?: string,
  externalDeviceMap?: ExternalDeviceMap,
  ceilingHeight?: number
) => {
  const linkIsMesh = linksLinkIsMeshGetter(devicesMap, link);
  const variant = resolveLinkVariant(devicesMap, link, externalDeviceMap);
  const { mlo, mloVariants, defaultApDevice } = linksLinkDevicesInfoGetter(
    devicesMap,
    linksMap,
    link,
    variant,
    country,
    externalDeviceMap
  );
  if (!mlo || !mloVariants || linkIsMesh) {
    const speed = linksLinkSpeedGetter(devicesMap, linksMap, link, variant, country, externalDeviceMap, ceilingHeight);
    return [speed];
  }
  const speeds: { speed: number; bandLabel: string }[] = [];
  mloVariants.forEach((variant) => {
    const variantDevice = deviceInstanceGetter(defaultApDevice.name, variant, externalDeviceMap);
    const bandLabel = bandLabelGetter(variantDevice, defaultApDevice.variantsMap);
    const speed = linksLinkSpeedGetter(devicesMap, linksMap, link, variant, country, externalDeviceMap, ceilingHeight);
    if (bandLabel) {
      speeds.push({ bandLabel, speed });
    }
  });
  return speeds.sort((a, b) => b.bandLabel.localeCompare(a.bandLabel)).map((speedEntry) => speedEntry.speed);
};

export const linksLinkChannelWidthsGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  country?: string,
  externalDeviceMap?: ExternalDeviceMap
) => {
  const linkIsMesh = linksLinkIsMeshGetter(devicesMap, link);
  const variant = resolveLinkVariant(devicesMap, link, externalDeviceMap);
  const { mlo, mloVariants, defaultApDevice, clientDeviceName } = linksLinkDevicesInfoGetter(
    devicesMap,
    linksMap,
    link,
    variant,
    country,
    externalDeviceMap
  );
  const defaultClientDevice = deviceInstanceGetter(clientDeviceName, undefined, externalDeviceMap);

  if (!mlo || !mloVariants || linkIsMesh) {
    const clientDevice = deviceInstanceGetter(
      clientDeviceName,
      resolveVariant(defaultClientDevice.variants, variant),
      externalDeviceMap
    );
    const channelWidth = resolveChannelWidth(
      clientDevice.channelWidths,
      linksDeviceChannelWidthGetter(devicesMap, linksMap, link, variant, externalDeviceMap)
    );
    if (!channelWidth) {
      return [];
    }
    return [channelWidth];
  }
  const channelWidths: { channelWidth: number; bandLabel: string }[] = [];
  const variantsMap = deviceInstanceGetter(defaultApDevice.name).variantsMap;
  mloVariants.forEach((variant) => {
    const variantDevice = deviceInstanceGetter(defaultApDevice.name, variant, externalDeviceMap);
    const clientDevice = deviceInstanceGetter(
      clientDeviceName,
      resolveVariant(defaultClientDevice.variants, variant),
      externalDeviceMap
    );
    const bandLabel = bandLabelGetter(variantDevice, variantsMap);
    const channelWidth = resolveChannelWidth(
      clientDevice.channelWidths,
      linksDeviceChannelWidthGetter(devicesMap, linksMap, link, variant, externalDeviceMap)
    );
    if (bandLabel && channelWidth) {
      channelWidths.push({ bandLabel, channelWidth });
    }
  });
  return channelWidths
    .sort((a, b) => b.bandLabel.localeCompare(a.bandLabel))
    .map((speedEntry) => speedEntry.channelWidth);
};

export const linksLinkMaxMcsGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  externalDeviceMap?: ExternalDeviceMap
) => {
  const { channelWidth, apDevice, clientDevice } = linksLinkDevicesInfoGetter(
    devicesMap,
    linksMap,
    link,
    undefined,
    undefined,
    externalDeviceMap
  );
  if (!channelWidth) {
    return undefined;
  }

  return calculateMaxMcs(channelWidth, apDevice, clientDevice);
};

export const linksLinkSpeedGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ApStateDevice['variant'],
  country?: string,
  externalDeviceMap?: ExternalDeviceMap,
  ceilingHeight?: number
) => {
  const { apDevice, clientDevice, channelWidth, channelUsage } = linksLinkDevicesInfoGetter(
    devicesMap,
    linksMap,
    link,
    variantArg,
    country,
    externalDeviceMap
  );
  if (!apDevice || !clientDevice || !channelWidth) {
    return 0;
  }

  const signal = linksLinkSignalGetter(
    devicesMap,
    linksMap,
    link,
    variantArg,
    country,
    externalDeviceMap,
    ceilingHeight
  );
  if (signal == null) {
    return 0;
  }

  return calculateCapacity({ signal, channelWidth, channelUsage, apDevice, clientDevice });
};

export const linksCombinedLinkSpeedCalculationGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ValuesOf<typeof DeviceVariantsEnum>,
  country?: string,
  externalDeviceMap?: ExternalDeviceMap,
  ceilingHeight?: number
) => {
  const { apDevice, mloVariants, mlo, variant } = linksLinkDevicesInfoGetter(
    devicesMap,
    linksMap,
    link,
    variantArg,
    country,
    externalDeviceMap
  );
  const meshVariant = devicesMeshDeviceVariantGetter(devicesMap);
  const linkIsMesh = linksLinkIsMeshGetter(devicesMap, link);
  const uplink = linksLinkMeshUplinkGetter(linksMap, link);
  let impactedByUplink = false;
  let capacity;
  let uplinkCapacity: number | null = null;
  const isMloCalculation = mloVariants && mlo && !linkIsMesh;
  if (uplink) {
    const uplinkDeviceName = devicesDeviceNameGetter(devicesMap[linksLinkApIdGetter(uplink)]);
    const uplinkDefaultInstance = deviceInstanceGetter(uplinkDeviceName);
    const channel = linksDeviceChannelGetter(devicesMap, linksMap, uplink, undefined, country, externalDeviceMap);
    const uplinkVariant = getMeshVariant(uplinkDefaultInstance.variants, channel);

    uplinkCapacity = linksCombinedLinkSpeedGetter(
      devicesMap,
      linksMap,
      uplink,
      uplinkVariant,
      country,
      externalDeviceMap,
      ceilingHeight
    );
  }
  if (isMloCalculation) {
    let mloIsImpacted = false;
    const capacities = mloVariants
      .map((variant) => {
        let capacity = linksLinkSpeedGetter(
          devicesMap,
          linksMap,
          link,
          variant,
          country,
          externalDeviceMap,
          ceilingHeight
        );
        if (variant === meshVariant && uplinkCapacity != null && uplinkCapacity + capacity !== 0) {
          const adjustedCapacity = Math.min((uplinkCapacity * capacity) / (uplinkCapacity + capacity), uplinkCapacity);
          if (adjustedCapacity < capacity) {
            mloIsImpacted = true;
          }
          capacity = adjustedCapacity;
        }
        return {
          capacity,
          variant,
        };
      })
      .sort((cap1, cap2) => cap2.capacity - cap1.capacity)
      .slice(0, 2);
    capacity = sumBy(capacities, 'capacity');
    impactedByUplink = map(capacities, 'variant').includes(meshVariant!) && mloIsImpacted;
  } else {
    capacity = linksLinkSpeedGetter(devicesMap, linksMap, link, variantArg, country, externalDeviceMap, ceilingHeight);
  }
  if (uplink && uplinkCapacity != null && uplinkCapacity + capacity !== 0) {
    const adjustedCapacity = Math.min(
      !isMloCalculation && variant === meshVariant ?
        (uplinkCapacity * capacity) / (uplinkCapacity + capacity)
      : capacity,
      uplinkCapacity
    );
    if (capacity > adjustedCapacity) {
      impactedByUplink = true;
    }
    capacity = adjustedCapacity;
  } else {
    capacity = Math.min(apDevice.maxEthThroughput, capacity);
  }
  return { capacity, impactedByUplink };
};

export const linksCombinedLinkSpeedGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ValuesOf<typeof DeviceVariantsEnum>,
  country?: string,
  externalDeviceMap?: ExternalDeviceMap,
  ceilingHeight?: number
): number => {
  const { capacity } = linksCombinedLinkSpeedCalculationGetter(
    devicesMap,
    linksMap,
    link,
    variantArg,
    country,
    externalDeviceMap,
    ceilingHeight
  );
  return capacity;
};

export const linksCombinedLinkSpeedImpactedByUplinkGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  country?: string,
  externalDeviceMap?: ExternalDeviceMap,
  ceilingHeight?: number
) => {
  const { impactedByUplink } = linksCombinedLinkSpeedCalculationGetter(
    devicesMap,
    linksMap,
    link,
    undefined,
    country,
    externalDeviceMap,
    ceilingHeight
  );
  return impactedByUplink;
};

export const linksMaxLinkSpeedGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  variantArg?: ApStateDevice['variant'],
  externalDeviceMap?: ExternalDeviceMap
) => {
  const { apDevice, clientDevice, channelWidth } = linksLinkDevicesInfoGetter(
    devicesMap,
    linksMap,
    link,
    variantArg,
    undefined,
    externalDeviceMap
  );
  if (apDevice && clientDevice && channelWidth) {
    return calculateMaxCapacity({ channelWidth, apDevice, clientDevice });
  }
  return 0;
};

export const linksMaxCombinedLinkSpeedGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  country?: string,
  externalDeviceMap?: ExternalDeviceMap
) => {
  const { mlo, mloVariants, apDevice } = linksLinkDevicesInfoGetter(
    devicesMap,
    linksMap,
    link,
    undefined,
    country,
    externalDeviceMap
  );
  const linkIsMesh = linksLinkIsMeshGetter(devicesMap, link);
  let capacity;
  if (mloVariants && mlo && !linkIsMesh) {
    const capacities = mloVariants
      .map((variant) => linksMaxLinkSpeedGetter(devicesMap, linksMap, link, variant, externalDeviceMap))
      .sort((cap1, cap2) => cap2 - cap1);
    capacity = capacities[0] + capacities[1];
  } else {
    capacity = linksMaxLinkSpeedGetter(devicesMap, linksMap, link, undefined, externalDeviceMap);
  }
  capacity = Math.min(apDevice.maxEthThroughput, capacity);
  return capacity;
};

export const linksDeviceVariantOptionsGetter = (
  devicesMap: DevicesMap,
  linksMap: Record<EntityId, StateLink>,
  link: StateLink,
  country = DEFAULT_COUNTRY,
  externalDeviceMap?: ExternalDeviceMap,
  ceilingHeight?: number
) => {
  const apId = linksLinkApIdGetter(link);
  const apStateDevice = devicesMap[apId];
  const apDeviceName = devicesDeviceNameGetter(apStateDevice);
  const apDeviceInstance = deviceInstanceGetter(apDeviceName, undefined, externalDeviceMap);
  const deviceVariants = apDeviceInstance.variantsMap;
  const isAfcEnabled = devicesDeviceAfcGetter(apStateDevice);
  const isOutdoorMode = devicesDeviceIsOutdoorModeGetter(apStateDevice);

  const clientId = linksLinkClientIdGetter(link);
  const clientStateDevice = devicesMap[clientId];
  const clientDeviceName = devicesDeviceNameGetter(clientStateDevice);
  const clientVariants = deviceInstanceGetter(clientDeviceName, undefined, externalDeviceMap).variants;

  const mlo = devicesDeviceMloGetter(apStateDevice);
  let mloVariants: ValuesOf<typeof DeviceVariantsEnum>[] | undefined;
  let activeMloVariants: ValuesOf<typeof DeviceVariantsEnum>[] | undefined;
  if (mlo) {
    mloVariants = apDeviceInstance.mloVariants?.filter((variant) =>
      countrySupportsBand(country, getBandByVariant(variant), isOutdoorMode, isAfcEnabled)
    );
    activeMloVariants = getMloVariants(mloVariants, devicesDeviceVariantGetter(apStateDevice));
  }
  const linkIsMesh = linksLinkIsMeshGetter(devicesMap, link);
  const meshVariants = get5GhzVariants(apDeviceInstance.variants);
  const variantsForBandTooltip = variantsFullBandNameGetter(apDeviceInstance.variants);

  const options: {
    label: string;
    value: string;
    infoContent?: string;
    isActive?: boolean;
    isDisabled?: boolean;
    isError?: boolean;
    tooltipDescription?: string;
  }[] = [];
  Object.entries(deviceVariants).forEach(([variant, variantDeviceName]) => {
    const numberVariant = Number(variant) as ValuesOf<typeof DeviceVariantsEnum>;
    // BLE radios have no user-configurable channel width or power; hide them from the variant picker.
    if (numberVariant === DeviceVariantsEnum.BLE) {
      return;
    }
    // TODO(mantas.grabauskas@ui.com): this should check device capabilities instead of hard coded 5 GHz
    if (linkIsMesh && !meshVariants.includes(numberVariant)) {
      return;
    }
    const variantDevice = deviceInstanceGetter(variantDeviceName, undefined, externalDeviceMap);
    const label = bandLabelGetter(variantDevice);
    if (label) {
      let isActive = false;
      let isDisabled = false;
      let isError = false;
      let tooltipDescription;
      let infoContent;
      if (isEnumValue(DeviceVariantsEnum, numberVariant)) {
        const band = getBandByVariant(numberVariant);

        if (variantsForBandTooltip.includes(numberVariant)) {
          tooltipDescription = bandLabelGetter(variantDevice, deviceVariants, true);
        }
        if (!isSupportedVariant(clientVariants, numberVariant)) {
          isDisabled = true;
          tooltipDescription = 'The selected client device does not support this frequency.';
        } else {
          if (!countrySupportsBand(country, band, isOutdoorMode, isAfcEnabled)) {
            isDisabled = true;
            const isEnabledIndoor = isOutdoorMode && countrySupportsBand(country, band, false, isAfcEnabled);
            tooltipDescription = `The selected country does not allow the use of this frequency${isEnabledIndoor ? ' in outdoor mode' : ''}.`;
          } else {
            isError =
              linksLinkSpeedGetter(
                devicesMap,
                linksMap,
                link,
                numberVariant,
                country,
                externalDeviceMap,
                ceilingHeight
              ) <= 0;
          }
        }

        if (mloVariants) {
          if (mloVariants.includes(numberVariant)) {
            if (activeMloVariants?.includes(numberVariant)) {
              isActive = true;
            }
          } else {
            isDisabled = true;
          }
        }
      }

      if (linkIsMesh) {
        infoContent = 'The mesh uplink operates in the 5 GHz band only';
      }

      options.push({
        label,
        value: variant,
        infoContent,
        isActive,
        isDisabled,
        isError,
        tooltipDescription,
      });
    }
  });

  options.sort((optionA, optionB) => Number(optionA.label.split(' ')[0]) - Number(optionB.label.split(' ')[0]));

  return options;
};

export const linksDeviceRadioLabelGetter = (
  deviceName: string,
  variant?: ValuesOf<typeof DeviceVariantsEnum>,
  externalDeviceMap?: ExternalDeviceMap
) => {
  const device = deviceInstanceGetter(deviceName, variant, externalDeviceMap);
  const wifiStandard = device.wifiStandard;

  const typeLabel = wifiStandard ? getWifiIEEEStandardBasedOnStandard(wifiStandard) : undefined;
  return `${wifiStandard}${typeLabel ? ` (${typeLabel})` : undefined}`;
};

export const linksLinkSignalValueGetter = (signal?: number, maxSignal?: number, minSignal?: number) => {
  if (signal == null || minSignal == null || maxSignal == null || signal < minSignal) {
    return 0;
  }
  if (signal > maxSignal) {
    return 100;
  }
  return Math.ceil(convertRange(signal, [minSignal, maxSignal], [0, 100]));
};

export const linksLinkIdsByRadioIdMapGetter = (links: Record<EntityId, StateLink>) => {
  const radioLinksMap: Record<string, string[]> = {};
  Object.values(links).forEach((link) => {
    const apId = linksLinkApIdGetter(link);
    const clientId = linksLinkClientIdGetter(link);
    const id = linksLinkIdGetter(link);

    radioLinksMap[apId] ??= [];
    radioLinksMap[apId].push(id);
    radioLinksMap[clientId] ??= [];
    radioLinksMap[clientId].push(id);
  });
  return radioLinksMap;
};

export const linksLinkIdsByClientIdMapGetter = (links: Record<EntityId, StateLink>) => {
  const clientLinksMap: Record<string, string> = {};
  Object.values(links).forEach((link) => {
    const clientId = linksLinkClientIdGetter(link);
    clientLinksMap[clientId] = linksLinkIdGetter(link);
  });
  return clientLinksMap;
};

const initialState = linksAdapter.upsertOne(emptyInitialState, { id: LINK_ID, apId: AP_ID, clientId: CLIENT_ID });

const linksSlice = createSlice({
  name: 'links',
  initialState,
  reducers: {
    linksLinksAddOrUpdate: linksAdapter.upsertMany, // oxlint-disable-line @typescript-eslint/unbound-method
    linksLinkRemove: linksAdapter.removeOne, // oxlint-disable-line @typescript-eslint/unbound-method
  },
});

export const { linksLinksAddOrUpdate, linksLinkRemove } = linksSlice.actions;

export const {
  selectById: linksLinkByIdSelector,
  selectEntities: linksLinkMapSelector,
  selectIds: linksLinkIdsSelector,
  selectAll: linksLinkAllSelector,
} = linksAdapter.getSelectors((state: RootState) => state.links);

export const getMeshPointPosition = (
  apPosition: StateDevice['position'],
  clientPosition: StateDevice['position'],
  minDistance = 5
) => {
  const midPoint = { x: (apPosition.x + clientPosition.x) / 2, y: (apPosition.y + clientPosition.y) / 2 };
  const distanceToMid = getDistance(apPosition, midPoint, undefined, undefined, undefined, true);
  if (distanceToMid >= minDistance) {
    return midPoint;
  }
  const perpendicularSlope = (apPosition.x - clientPosition.x) / (clientPosition.y - apPosition.y);
  const perpendicularDistance = Math.sqrt(minDistance ** 2 - distanceToMid ** 2);
  if (perpendicularSlope === 0) {
    return { x: midPoint.x + perpendicularDistance, y: midPoint.y };
  }
  if (!isFinite(perpendicularSlope)) {
    return { x: midPoint.x, y: midPoint.y + perpendicularDistance };
  }
  const dx = perpendicularDistance / Math.sqrt(1 + perpendicularSlope ** 2);
  const dy = perpendicularSlope * dx;
  return { x: midPoint.x + dx, y: midPoint.y + dy };
};

export const linksAddMeshPoint: () => AppThunk = () => (dispatch, getState) => {
  const state = getState();
  const apStateDevice = devicesDeviceByIdSelector(state, AP_ID) as ApStateDevice;
  const clientDeviceName = devicesDeviceNameSelector(state, CLIENT_ID);
  const clientPosition = devicesDevicePositionSelector(state, CLIENT_ID);
  const apDevice = devicesDeviceInstanceSelector(state, AP_ID);
  const meshDeviceName = resolveMeshDevice(apDevice.wifiStandard);
  const meshStateDevice = getInitialApDeviceState(
    MESH_AP_ID,
    meshDeviceName,
    clientDeviceName,
    apStateDevice,
    settingsCountrySelector(state),
    devicesCustomClientDeviceInstanceMapSelector(state)
  );
  meshStateDevice.position = getMeshPointPosition(devicesDevicePositionGetter(apStateDevice), clientPosition);
  useEditorStore
    .getState()
    .addDevice(MESH_AP_ID, { position: [meshStateDevice.position.x, meshStateDevice.position.y, 0] });
  dispatch(
    linksLinksAddOrUpdate({
      [LINK_ID]: {
        apId: MESH_AP_ID,
        clientId: CLIENT_ID,
        id: LINK_ID,
      },
      [MESH_LINK_ID]: { apId: AP_ID, clientId: MESH_AP_ID, id: MESH_LINK_ID },
    })
  );
  // dispatch order is important, links/middleware listening for deviceAdd to check if needs to do link reconnect
  dispatch(devicesDeviceAdd(meshStateDevice));
};

export const linksLinkIdsByRadioIdMapSelector = createSelector(linksLinkMapSelector, linksLinkIdsByRadioIdMapGetter);

export const linksLinkIdsByRadioIdSelector = (state: RootState, id: string) =>
  linksLinkIdsByRadioIdMapSelector(state)[id];

export const linksLinkIdsByClientIdMapSelector = createSelector(linksLinkMapSelector, linksLinkIdsByClientIdMapGetter);
export const linksUplinkIdByClientIdSelector = (state: RootState, id: string) =>
  linksLinkIdsByClientIdMapSelector(state)[id];

export const linksLinkApIdSelector = (state: RootState, id: string) =>
  linksLinkApIdGetter(linksLinkByIdSelector(state, id));

export const linksLinkClientIdSelector = (state: RootState, id: string) =>
  linksLinkClientIdGetter(linksLinkByIdSelector(state, id));

export const linksLinkIsMeshSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkByIdSelector,
  linksLinkIsMeshGetter
);

export const linksLinkSignalSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  optionalThirdArg,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  settingsCeilingHeightSelector,
  linksLinkSignalGetter
);

const linksActiveLinkSelector = createSelector(linksLinkMapSelector, activeElementLinkIdSelector, get);

export const linksClientLinkSelector = createSelector(linksLinkAllSelector, linksClientLinkGetter);

export const linksLinkVariantSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkByIdSelector,
  devicesCustomClientDeviceInstanceMapSelector,
  resolveLinkVariant
);

export const linksClientLinkApVariantSelector = createSelector(
  devicesDeviceMapSelector,
  linksActiveLinkSelector,
  resolveLinkApVariant
);

const linksPointerActiveLinkSelector = createSelector(
  devicesDeviceApIdsSelector,
  secondArg,
  linksLinkMapSelector,
  linksClientLinkSelector,
  devicesCustomClientDeviceInstanceMapSelector,
  settingsCountrySelector,
  settingsCeilingHeightSelector,
  linksPointerActiveLinkGetter
);

export const linksLinkPointerSignalSelector = createSelector(
  secondArg,
  linksLinkMapSelector,
  linksPointerActiveLinkSelector,
  linksClientLinkApVariantSelector,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  settingsCeilingHeightSelector,
  linksLinkSignalGetter
);

export const linksLinkMinSignalSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  devicesCustomClientDeviceInstanceMapSelector,
  linksLinkMinSignalGetter
);

export const linksLinkSignalLabelSelector = createSelector(
  linksLinkSignalSelector,
  linksLinkMinSignalSelector,
  (signal, minSignal) =>
    signal != null && (minSignal == null || signal >= minSignal) ? `${Math.ceil(signal)} dBm` : '-'
);

export const linksLinkSignalIsErrorSelector = createSelector(
  linksLinkSignalSelector,
  linksLinkMinSignalSelector,
  (signal, minSignal) => signal != null && minSignal != null && signal < minSignal
);

export const linksLinkMaxSignalSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  devicesCustomClientDeviceInstanceMapSelector,
  linksLinkMaxSignalGetter
);

export const linksLinkSignalValueSelector = createSelector(
  linksLinkSignalSelector,
  linksLinkMaxSignalSelector,
  linksLinkMinSignalSelector,
  linksLinkSignalValueGetter
);

export const linksLinkMcsSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  optionalThirdArg,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  settingsCeilingHeightSelector,
  linksLinkMcsGetter
);

export const linksLinkPointerMcsSelector = createSelector(
  secondArg,
  linksLinkMapSelector,
  linksPointerActiveLinkSelector,
  linksClientLinkApVariantSelector,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  settingsCeilingHeightSelector,
  linksLinkMcsGetter
);

export const linksLinkSignalLabelParamsSelector = createSelector(linksLinkMcsSelector, (mcs) => {
  if (mcs == null) {
    return undefined;
  }
  let label = 'Excellent';
  let color: 'positive' | 'notice' | 'destructive' = 'positive';
  if (mcs < 3) {
    label = 'Poor';
    color = 'destructive';
  } else if (mcs < 7) {
    label = 'Good';
    color = 'notice';
  }
  return { label, color };
});

export const linksLinkPhyRateSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  optionalThirdArg,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  settingsCeilingHeightSelector,
  linksLinkPhyRateGetter
);

export const linksLinkMaxMcsSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  devicesCustomClientDeviceInstanceMapSelector,
  linksLinkMaxMcsGetter
);

export const linksLinkSpeedSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  optionalThirdArg,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  settingsCeilingHeightSelector,
  linksCombinedLinkSpeedGetter
);

export const linksLinkSpeedImpactedByUplinkSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  settingsCeilingHeightSelector,
  linksCombinedLinkSpeedImpactedByUplinkGetter
);

export const linksMaxLinkSpeedSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  linksMaxCombinedLinkSpeedGetter
);

export const linksDeviceVariantOptionsSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  settingsCeilingHeightSelector,
  linksDeviceVariantOptionsGetter
);

export const linksDeviceChannelWidthSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  optionalThirdArg<ValuesOf<typeof DeviceVariantsEnum>>,
  devicesCustomClientDeviceInstanceMapSelector,
  linksDeviceChannelWidthGetter
);

export const linksHasMeshUplinkSelector = createSelector(
  linksLinkMapSelector,
  linksLinkByIdSelector,
  linksHasMeshUplinkGetter
);

export const linksMeshUplinkApIdSelector = createSelector(
  linksLinkMapSelector,
  linksLinkByIdSelector,
  linksMeshUplinkApIdGetter
);

export const linksMeshUplinkIdSelector = createSelector(
  linksLinkMapSelector,
  linksLinkByIdSelector,
  linksMeshUplinkIdGetter
);

export const linksDeviceChannelSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  optionalThirdArg<ValuesOf<typeof DeviceVariantsEnum>>,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  linksDeviceChannelGetter
);

export const linksIsChannelAutoSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  optionalThirdArg<ValuesOf<typeof DeviceVariantsEnum>>,
  linksIsChannelAutoGetter
);

export const linksDeviceFrequencySelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  optionalThirdArg,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  linksDeviceFrequencyGetter
);

export const linksDeviceRadioLabelSelector = createSelector(
  devicesDeviceNameSelector,
  devicesDeviceVariantSelector,
  devicesCustomClientDeviceInstanceMapSelector,
  linksDeviceRadioLabelGetter
);

export const linksLinkPhyRatesMapSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  settingsCeilingHeightSelector,
  linksLinkPhyRatesMapGetter
);

export const linksLinkAirSpeedsSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  settingsCeilingHeightSelector,
  linksLinkAirSpeedsGetter
);
export const linksLinkChannelWidthsSelector = createSelector(
  devicesDeviceMapSelector,
  linksLinkMapSelector,
  linksLinkByIdSelector,
  settingsCountrySelector,
  devicesCustomClientDeviceInstanceMapSelector,
  linksLinkChannelWidthsGetter
);

export default linksSlice.reducer;
